import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { DataStateChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CommonService } from 'src/app-core/services/common.service';
import { QuotesService } from 'src/app/quotes/quotes.service';
import { AppComponentBase } from 'src/app/shared/app-component-base';
import { DocumentTypeIdsEnum } from '../app-common-enum';
import { CommonDataModel } from '../common-data-model';
import { OrderEmailPopupFormComponent } from '../order-email-popup-form/order-email-popup-form.component';
import { AccountDocumentDialogComponent } from './account-document-dialog/account-document-dialog.component';

@Component({
    selector: 'app-orders-documents-list',
    templateUrl: './orders-documents-list.component.html',
    styleUrls: ['./orders-documents-list.component.scss']
})
export class OrdersDocumentsListComponent extends AppComponentBase implements OnInit {

    @ViewChild('grid', { static: true }) private grid: GridComponent;
    @Input() orderId: string;
    @Input() documentTypeId: number;
    @Input() set orderData(orderData: any) {
        if (orderData) {
            this.orderDetails = orderData;
        }
    }
    @Input() set documentConfigs(documentConfigs: any) {
        if (documentConfigs) {
            this.documentConfigData = documentConfigs;
        }
    }
    @Input() set documentsTitle(documentsTitle: any) {
        if (documentsTitle) {
            this.title = documentsTitle;
        }
    }
    @Output() documentsList = new EventEmitter<string>();
    commonDataModel = new CommonDataModel();
    documentTypeEnum: typeof DocumentTypeIdsEnum = DocumentTypeIdsEnum;
    title = 'All Documents';
    documentsCount: number;
    documentConfigData = this.commonDataModel.documentConfigModel;
    orderDetails: any;
    isFormLoading = false;
    accountID: number;
    constructor(
        injector: Injector,
        private dialog: MatDialog,
        private commonService: CommonService,
        private activatedRoute: ActivatedRoute,
        private toastr: ToastrService,
        private quotesService: QuotesService) {
        super(injector);
    }

    ngOnInit() {
        if (!this.orderId) {
            this.orderId = (this.activatedRoute.snapshot.queryParams.quoteId) ?
                this.activatedRoute.snapshot.queryParams.quoteId :
                this.activatedRoute.snapshot.queryParams.jobId;
        }
        this.onRefreshGridData();
    }

    onRefreshGridData(): void {
        this.kendogridHelper.sort = [{ dir: 'desc', field: 'name' }];
        this.grid.dataStateChange.emit({
            skip: 0,
            take: this.kendogridHelper.defaultRecordsCountPerPage,
            filter: undefined,
            group: undefined,
            sort: this.kendogridHelper.sort,
        });
    }

    getGridRecords(event: DataStateChangeEvent): void {
        if (event) {
            this.kendogridHelper.dataStateChangeEvent(event);
        }
        this.kendogridHelper.showLoadingIndicator();
        switch (this.title) {
            case 'House Bill':
                this.documentTypeId = this.documentTypeEnum.houseBill;
                break;
            case 'Cut Sheet':
                this.documentTypeId = this.documentTypeEnum.cutSheet;
                break;
            case 'BOL':
                this.documentTypeId = this.documentTypeEnum.bol;
                break;
            case 'Quote':
                this.documentTypeId = this.documentTypeEnum.quote;
                break;
            case 'Shipping Label':
                this.documentTypeId = this.documentTypeEnum.shippingLabel;
                break;
            case 'Invoice':
                this.documentTypeId = this.documentTypeEnum.invoice;
                break;
            case 'Request For Price':
                this.documentTypeId = this.documentTypeEnum.requestForPrice;
                break;
            case 'Notification Of Arrival':
                this.documentTypeId = this.documentTypeEnum.notificationOfArrival;
                break;
            case 'Uploaded Documents':
                this.documentTypeId = this.documentTypeEnum.custom;
                break;
            default:
                this.documentTypeId = null;
                break;
        }
        let docTypeQuery = '';
        if (this.documentTypeId) {
            docTypeQuery = `&DocumentTypeId=${this.documentTypeId}`;
        }
        this.commonService.getOrderDocumentsGridRecords(event, `${docTypeQuery}&OrderId=${this.orderId}`, '')
            .pipe(finalize(() => (this.kendogridHelper.hideLoadingIndicator()))).subscribe(response => {
                if (response.isSuccess) {
                    this.documentsList.emit(response.data);
                    this.kendogridHelper.setGridData(response.data);
                    this.documentsCount = response.data.items.length;
                    this.kendogridHelper.hideLoadingIndicator();
                }
            });
    }

    getDocumentTypeIdForEmailFromAllGrid(documentCategory: string) {
        switch (documentCategory) {
            case 'HouseBill':
                return this.documentTypeEnum.houseBill;
            case 'CutSheet':
                return this.documentTypeEnum.cutSheet;
            case 'BOL':
                return this.documentTypeEnum.bol;
            case 'Quote':
                return this.documentTypeEnum.quote;
            case 'Estimate':
                return this.documentTypeEnum.estimate;
            case 'ShippingLabel':
                return this.documentTypeEnum.shippingLabel;
            case 'Invoice':
                return this.documentTypeEnum.invoice;
            case 'RequestForPrice':
                return this.documentTypeEnum.requestForPrice;
            case 'NotificationOfArrival':
                return this.documentTypeEnum.notificationOfArrival;
            case 'Custom':
                return this.documentTypeEnum.custom;
            default:
                return null;
        }
    }

    onRemoveDocument(documentId: string) {
        this.commonService.deleteOrderDocument({ documentId, isActive: false }).subscribe((response: any) => {
            if (response.isSuccess) {
                this.toastr.success('Document Deleted successfully');
                this.onRefreshGridData();
            }
        });
    }

    onDownloadAllDocument() {
        this.isFormLoading = true;
        const documentTypeId = this.title === 'All Documents' ? 0 : this.getDocumentTypeIdForDownloadAll(this.title);
        this.commonService.getAllDocumentFile(documentTypeId, this.orderId)
        .pipe(finalize(() => (this.isFormLoading = false)))
        .subscribe((response: any) => {
            if (response.status === 200) {
                const data: any = response;
                this.commonService.downloadBolbMultipleFiles([
                    {
                        body: data.body,
                        options: { type: data.body.type },
                        filename: this.title
                    }
                ]);
            }
        });
    }

    getDocumentTypeIdForDownloadAll(title: string) {
        const categoryMap = {
            'House Bill': this.documentTypeEnum.houseBill,
            'Cut Sheet': this.documentTypeEnum.cutSheet,
            'BOL': this.documentTypeEnum.bol,
            'Quote': this.documentTypeEnum.quote,
            'Estimate': this.documentTypeEnum.estimate,
            'Shipping Label': this.documentTypeEnum.shippingLabel,
            'Invoice': this.documentTypeEnum.invoice,
            'Request For Price': this.documentTypeEnum.requestForPrice,
            'Notification Of Arrival': this.documentTypeEnum.notificationOfArrival,
            'Uploaded Documents': this.documentTypeEnum.custom
        };
    
        return categoryMap[title] || null;
    }

    onDownloadDocument(dataItem: any) {
        this.commonService.getDocumentFile(dataItem.id).subscribe((response: any) => {
            if (response.status === 200) {
                const data: any = response;
                this.commonService.downloadBolbFile(data.body as any, { type: data.body.type }, `${dataItem.name}`);
            }
        });
    }

    onViewDocument(dataItem: any) {
        window.open(dataItem.blobUrl, '_blank');
    }

    onEmail(dataItem: any) {
        if (this.title === 'All Documents' || this.title === 'Quote') {
            this.documentTypeId = this.getDocumentTypeIdForEmailFromAllGrid(dataItem.documentCategory);
            if (dataItem.documentCategory === 'Custom') {
                this.documentConfigData.subject = 'User Uploaded document';
            }
            this.openEmailPopup(dataItem);
        } else {
            if (dataItem.documentCategory === 'Custom') {
                this.documentConfigData.subject = 'User Uploaded document';
            }
            this.openEmailPopup(dataItem);
        }
    }

    openEmailPopup(dataItem: any) {
        const documentTypeId = this.getDocumentTypeIdForEmailFromAllGrid(dataItem.documentCategory);
        this.isFormLoading = true;
        this.commonService.getDocumentConfig(this.orderId, documentTypeId).subscribe(response => {
            if (response.isSuccess) {
                this.documentConfigs = _.cloneDeep(response.data);
                this.quotesService.getQuote(this.orderId).subscribe((resp: any) => {
                    this.isFormLoading = false;
                    if (resp.isSuccess) {
                        this.orderDetails = resp.data;
                        this.openEmailpopupContinue(dataItem);
                    }
                });
            }
        });
    }

    openEmailpopupContinue(dataItem: any) {
        this.dialog.open(OrderEmailPopupFormComponent, {
            position: {
                right: '0',
            },
            panelClass: 'small_layout',
            hasBackdrop: false,
            autoFocus: false,
            data: {
                documentConfigData: this.documentConfigData,
                orderId: this.orderId,
                documentDetails: dataItem,
                orderDetails: this.orderDetails
            },
        }).afterClosed()
            .subscribe((response: { confirmResult: boolean }) => {
                if (response && response.confirmResult === true) {
                    this.onRefreshGridData();
                }
            });
    }

    onAccountDocumentDialogClick() {
        this.accountID = this.quotesService.getAccountID();
        this.dialog.open(AccountDocumentDialogComponent, {
            position: {
                right: '0',
            },
            panelClass: ['center_layout', 'full_min_height'],
            hasBackdrop: false,
            data: {
                accountID: this.accountID
            },
            autoFocus: false
        }).afterClosed()
            .subscribe((response: any) => {
                this.onRefreshGridData();
            });
    }

}
